import { useEffect, useState } from "react";
import { Field, TextInput, Button } from "../components/ui";
import { subscribeLegalPage, saveLegalPage } from "../lib/legalPages";

const PAGES = [
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "terms-and-conditions", label: "Terms & Conditions" },
];

function formatTimestamp(ts) {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export default function LegalPagesEditor() {
  const [activeSlug, setActiveSlug] = useState(PAGES[0].slug);
  const [page, setPage] = useState(undefined);
  const [form, setForm] = useState({ title: "", content: "", effectiveDate: "", status: "draft" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setPage(undefined);
    setError(null);
    setSavedAt(null);
    return subscribeLegalPage(activeSlug, (doc) => {
      setPage(doc);
      setForm({
        title: doc?.title || "",
        content: doc?.content || "",
        effectiveDate: doc?.effectiveDate || "",
        status: doc?.status || "draft",
      });
    });
  }, [activeSlug]);

  const save = async (status) => {
    setSaving(true);
    setError(null);
    try {
      await saveLegalPage(activeSlug, { ...form, status });
      setForm((f) => ({ ...f, status }));
      setSavedAt(new Date());
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-navy/10 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy/8 px-5 py-4">
        <h2 className="text-lg font-bold text-navy">Legal Pages</h2>
        <div className="flex gap-2">
          {PAGES.map((p) => (
            <button
              key={p.slug}
              type="button"
              onClick={() => setActiveSlug(p.slug)}
              className={`focus-ring rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
                activeSlug === p.slug
                  ? "bg-navy text-white"
                  : "bg-navy/5 text-navy/60 hover:bg-navy/10"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {page === undefined ? (
        <p className="px-5 py-8 text-navy/40">Loading…</p>
      ) : (
        <div className="flex flex-col gap-5 px-5 py-6">
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-navy/40">
            <span>
              Status:{" "}
              <span className={form.status === "published" ? "text-success" : "text-primary"}>
                {form.status === "published" ? "Published (live)" : "Draft (not visible to users)"}
              </span>
            </span>
            {page && <span>· Version {page.version}</span>}
            {page && <span>· Last updated {formatTimestamp(page.updatedAt)}</span>}
          </div>

          <Field label="Title" id="legal-title">
            <TextInput
              id="legal-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Field>

          <Field label="Effective date" id="legal-date">
            <TextInput
              id="legal-date"
              type="date"
              value={form.effectiveDate}
              onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
            />
          </Field>

          <Field label="Content" id="legal-content">
            <p className="mb-1 text-xs text-navy/40">
              Use <code>## Heading</code> for section headings, <code>- item</code> for lists, and{" "}
              <code>**bold**</code> for emphasis. Blank lines start a new paragraph.
            </p>
            <textarea
              id="legal-content"
              rows={24}
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              className="focus-ring w-full rounded-xl border-2 border-navy/12 bg-white px-4 py-3 font-mono text-xs leading-relaxed text-navy focus:border-primary"
            />
          </Field>

          {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
          {savedAt && !error && (
            <p className="text-sm font-semibold text-success">Saved at {savedAt.toLocaleTimeString("en-IN")}.</p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" disabled={saving} onClick={() => save("draft")}>
              Save as Draft
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => save("published")}>
              Save & Publish
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
